import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

interface PublishConfirmDialogProps {
  open: boolean;
  /** Name as it will be saved, so the confirm names the actual thing. */
  listName: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The one irreversible step in the desk flow.
 *
 * Saving with "Publish to the Newsquawk library" on is live immediately: the
 * desk and customer apps share one pref-storage instance, so the list is in
 * every customer's library the moment it saves, and it syncs onward to the
 * filtering service. There is no draft state to fall back to and ownership
 * cannot be changed afterwards — the record would have to be deleted and
 * rebuilt.
 *
 * Shown only on the transition to published, never on later edits to a list
 * that is already in the library.
 */
export default function PublishConfirmDialog({
  open,
  listName,
  onCancel,
  onConfirm,
}: PublishConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} className="no-drag relative z-[70]">
      <DialogBackdrop className="fixed inset-0 bg-black/60" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
          <div className="flex items-start gap-3 px-4 py-4">
            <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-400" />
            <div className="min-w-0">
              <DialogTitle className="text-sm font-semibold text-gray-100">
                Publish &ldquo;{listName}&rdquo; to the Newsquawk library?
              </DialogTitle>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">
                Every customer will see it as soon as you save. There is no
                draft state, and it can&apos;t be moved back to a personal
                watchlist afterwards — that would mean deleting and rebuilding
                it.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-gray-700/50 px-4 py-3">
            <button
              type="button"
              onClick={onCancel}
              className="cursor-pointer rounded border border-gray-600 bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 transition-colors hover:border-gray-500 hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="cursor-pointer rounded border border-squawk-blue bg-squawk-blue/15 px-3 py-1.5 text-xs font-semibold text-squawk-blue transition-colors hover:bg-squawk-blue/25"
            >
              Publish to library
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
